/**
 * Copy for the sync line at the top of the dashboard Contacts panel: whether
 * the phone has applied the contact list, and when.
 *
 * Kept here rather than in en.json/fr.json for the same reason as
 * features/contactPhoto/contactPhotoText.ts: fallbackLng is 'fr', so an
 * EN-only key would render as a raw key, and fr.json is frozen (see
 * .claude/rules/fr-freeze-pin.md). A string with no French falls back to
 * English.
 *
 * NEVER invent French here. FR copy comes from Normand, verbatim.
 * Record: docs/plans/2026-07-29-fr-items-for-normand.md, items S1–S9.
 */
const en = {
  phoneUpdated: 'Phone updated · {{time}}', // S1 — revised by Normand 2026-10-03
  lastUpdate: 'Last update {{time}}', // S2 — revised by Normand 2026-10-03
  waitingForPhone: 'Waiting for phone to confirm', // S3
  onPhone: '{{count}} on phone', // S4
  justNow: 'just now', // S5
  secondsAgo: '{{n}}s ago', // S6
  minutesAgo: '{{n}}m ago', // S7
  hoursAgo: '{{n}}h ago', // S8
  daysAgo: '{{n}}d ago', // S9
} as const;

export type ContactsSyncTextKey = keyof typeof en;

// Normand-supplied translations only, VERBATIM: supplied in chat 2026-10-03 as
// S1–S9. Do not correct, re-punctuate or "normalise" them (the spaces in S6–S9
// are ordinary spaces, as supplied).
const fr: Partial<Record<ContactsSyncTextKey, string>> = {
  phoneUpdated: 'Le téléphone est mis à jour · {{time}}', // S1
  lastUpdate: 'Dernière mise à jour {{time}}', // S2
  waitingForPhone: 'En attente de confirmation du téléphone', // S3
  onPhone: '{{count}} sur le téléphone', // S4
  justNow: "à l'instant", // S5
  secondsAgo: 'il y a {{n}} s', // S6
  minutesAgo: 'il y a {{n}} min', // S7
  hoursAgo: 'il y a {{n}} h', // S8
  daysAgo: 'il y a {{n}} j', // S9
};

const byLanguage: Record<string, Partial<Record<ContactsSyncTextKey, string>>> = { en, fr };

/** Keys with no translation in `language` (they show in English). */
export function untranslatedContactsSyncKeys(language: string): ContactsSyncTextKey[] {
  const dict = byLanguage[language.split('-')[0]];
  return (Object.keys(en) as ContactsSyncTextKey[]).filter((k) => dict?.[k] === undefined);
}

export function contactsSyncText(
  language: string | undefined,
  key: ContactsSyncTextKey,
  vars?: Record<string, string | number>,
): string {
  const lang = (language || 'en').split('-')[0];
  const raw = byLanguage[lang]?.[key] ?? en[key];
  return vars ? raw.replace(/\{\{(\w+)\}\}/g, (_, name: string) => String(vars[name] ?? '')) : raw;
}

/**
 * How long ago the phone applied the list (S5–S9). Empty for a missing or
 * future timestamp, so the line never shows a negative age.
 */
export function formatSyncAgo(iso: string | null, language: string | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const sec = Math.floor(ms / 1000);
  if (sec < 30) return contactsSyncText(language, 'justNow');
  if (sec < 60) return contactsSyncText(language, 'secondsAgo', { n: sec });
  const min = Math.floor(sec / 60);
  if (min < 60) return contactsSyncText(language, 'minutesAgo', { n: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return contactsSyncText(language, 'hoursAgo', { n: hr });
  return contactsSyncText(language, 'daysAgo', { n: Math.floor(hr / 24) });
}
